import { useState } from "react";
import { X, ExternalLink, Shield, CheckCircle, AlertTriangle, Info, Calendar, Globe } from "lucide-react";

interface SourceVerificationModalProps {
  open: boolean;
  onClose: () => void;
  alert: {
    id: string;
    title: string;
    source_type: string;
    created_at: string;
    description: string;
  };
}

export default function SourceVerificationModal({ open, onClose, alert }: SourceVerificationModalProps) {
  if (!open) return null;

  const getSourceDetails = (sourceType: string) => {
    // Real government sources
    if (sourceType.includes('fews_net')) {
      return {
        isVerified: true,
        organizationName: 'FEWS NET (Famine Early Warning Systems Network)',
        organizationType: 'International Food Security Organization',
        credibility: 'Highly Trusted',
        description: 'FEWS NET provides early warning and analysis on food insecurity. It is funded by USAID and provides objective, evidence-based analysis.',
        website: 'https://fews.net',
        verificationLevel: 'Government Verified',
        icon: CheckCircle,
        color: 'green'
      };
    }
    
    if (sourceType.includes('reliefweb')) {
      return {
        isVerified: true,
        organizationName: 'ReliefWeb (UN Office for Coordination of Humanitarian Affairs)',
        organizationType: 'United Nations Humanitarian Information Service',
        credibility: 'Highly Trusted',
        description: 'ReliefWeb is the leading humanitarian information source on global crises and disasters. It is a specialized digital service of the UN Office for the Coordination of Humanitarian Affairs.',
        website: 'https://reliefweb.int',
        verificationLevel: 'UN Verified',
        icon: CheckCircle,
        color: 'green'
      };
    }
    
    if (sourceType.includes('world_bank')) {
      return {
        isVerified: true,
        organizationName: 'World Bank Group',
        organizationType: 'International Financial Institution',
        credibility: 'Highly Trusted',
        description: 'The World Bank provides development assistance and agricultural programs. Their data comes from official government partnerships and field research.',
        website: 'https://worldbank.org',
        verificationLevel: 'International Organization',
        icon: CheckCircle,
        color: 'green'
      };
    }
    
    if (sourceType.includes('ministry_agriculture_ls')) {
      return {
        isVerified: true,
        organizationName: 'Ministry of Agriculture and Food Security - Lesotho',
        organizationType: 'Government of Lesotho',
        credibility: 'Official Government Source',
        description: 'Official agricultural ministry of the Kingdom of Lesotho. Responsible for agricultural policy, food security, and farmer support programs.',
        website: 'https://gov.ls',
        verificationLevel: 'Government Official',
        icon: CheckCircle,
        color: 'green'
      };
    }
    
    // Sample data sources
    if (sourceType.includes('ministry_of_agriculture') || sourceType.includes('government_program')) {
      return {
        isVerified: false,
        organizationName: 'Sample Government Data',
        organizationType: 'Demonstration Data',
        credibility: 'Sample Only',
        description: 'This is sample data created for demonstration purposes. It represents the type of alerts that would come from government sources.',
        website: null,
        verificationLevel: 'Sample Data',
        icon: AlertTriangle,
        color: 'yellow'
      };
    }
    
    // System generated
    if (sourceType.includes('agrosense') || sourceType.includes('weather_service')) {
      return {
        isVerified: false,
        organizationName: 'AgroSense System',
        organizationType: 'Agricultural Advisory Platform',
        credibility: 'System Generated',
        description: 'This alert was generated by the AgroSense system based on weather data and agricultural patterns. Not from government sources.',
        website: null,
        verificationLevel: 'System Alert',
        icon: Info,
        color: 'blue'
      };
    }
    
    // Default
    return {
      isVerified: false,
      organizationName: 'Unknown Source',
      organizationType: 'Unverified',
      credibility: 'Not Verified',
      description: 'Source verification information not available.',
      website: null,
      verificationLevel: 'Unverified',
      icon: Shield,
      color: 'gray'
    };
  };

  const sourceDetails = getSourceDetails(alert.source_type);
  const Icon = sourceDetails.icon;

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'green':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          text: 'text-green-800',
          icon: 'text-green-600'
        };
      case 'yellow':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          text: 'text-yellow-800',
          icon: 'text-yellow-600'
        };
      case 'blue':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          text: 'text-blue-800',
          icon: 'text-blue-600'
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          text: 'text-gray-800',
          icon: 'text-gray-600'
        };
    }
  };

  const colors = getColorClasses(sourceDetails.color);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Shield className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Source Verification</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Alert Info */}
          <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="font-semibold text-gray-900 mb-2">Alert Information</h3>
            <p className="text-sm text-gray-700 mb-2">{alert.title}</p>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <Calendar className="w-3 h-3" />
                <span>Published: {new Date(alert.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Globe className="w-3 h-3" />
                <span>Source: {alert.source_type.replace(/_/g, ' ')}</span>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className={`p-4 rounded-lg border ${colors.bg} ${colors.border}`}>
            <div className="flex items-center space-x-3 mb-3">
              <Icon className={`w-6 h-6 ${colors.icon}`} />
              <div>
                <h3 className={`font-semibold ${colors.text}`}>
                  {sourceDetails.verificationLevel}
                </h3>
                <p className={`text-sm ${colors.text}`}>
                  Credibility: {sourceDetails.credibility}
                </p>
              </div>
            </div>
          </div>

          {/* Organization Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Organization Details</h3>
            
            <div className="grid gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600">Organization Name</label>
                <p className="text-gray-900">{sourceDetails.organizationName}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">Type</label>
                <p className="text-gray-900">{sourceDetails.organizationType}</p>
              </div>
              
              <div>
                <label className="text-sm font-medium text-gray-600">About</label>
                <p className="text-gray-700 text-sm leading-relaxed">{sourceDetails.description}</p>
              </div>
              
              {sourceDetails.website && (
                <div>
                  <label className="text-sm font-medium text-gray-600">Official Website</label>
                  <a
                    href={sourceDetails.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <span>{sourceDetails.website}</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-800 mb-2">How to Verify</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Check if the source has an official website</li>
              <li>• Look for government or UN verification badges</li>
              <li>• Cross-reference with other trusted sources</li>
              <li>• Verify publication dates are recent</li>
              {sourceDetails.isVerified && (
                <li className="font-medium">✅ This source has been verified by AgroSense</li>
              )}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Close
          </button>
          {sourceDetails.website && (
            <a
              href={sourceDetails.website}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <span>Visit Source</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}